import { useState, type ReactNode } from 'react'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { formatScannerSourceLabel } from '../../services/vulnerabilityPostureApi'
import { useI18n } from '../../i18n'

export type AssessmentScopeDto = {
  id?: string | null
  scannerScanId?: string | null
  provider?: string | null
  target?: string | null
  scanType?: string | null
  profile?: string | null
  status?: string | null
  startedAt?: string | null
  completedAt?: string | null
  durationSeconds?: number | null
  coverage?: {
    analyzedTargets?: number | null
    analyzedHosts?: number | null
    analyzedUrls?: number | null
    requestsSent?: number | null
    pagesCrawled?: number | null
    openPorts?: number | null
  } | null
  counts?: {
    uniqueFindings?: number
    affectedInstances?: number
    rawOccurrences?: number
    critical?: number
    high?: number
    medium?: number
    low?: number
    info?: number
  } | null
  countingMode?: {
    primary?: string
    uniqueSeverityDistribution?: Record<string, number> | null
    instanceSeverityDistribution?: Record<string, number> | null
    rawOccurrenceSeverityDistribution?: Record<string, number> | null
  } | null
  parserStatus?: string | null
  authoritativeZero?: boolean
  zeroFindingsMessage?: string | null
  // legacy AssessmentCoverageCard shape
  scanner?: string | null
  providerType?: string | null
  finishedAt?: string | null
  templateProfile?: string | null
  findings?: {
    critical?: number
    high?: number
    medium?: number
    low?: number
    info?: number
    total?: number
  } | null
  coverageEstablished?: boolean
}

function fmtDate(value?: string | null) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

function fmtMetric(value: number | null | undefined) {
  if (value == null) return '—'
  return value
}

function fmtDuration(seconds: number | null | undefined) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return null
  const s = Math.max(0, Math.round(Number(seconds)))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return rem ? `${m}m ${rem}s` : `${m}m`
}

type Props = {
  assessment: AssessmentScopeDto | null | undefined
  compact?: boolean
  /** Explicit counting mode label for Result section */
  countingModeLabel?: string
}

/**
 * Assessment Summary — THIS SCAN ONLY.
 * Single primary identity + coverage + finding counts block.
 * Never displays historical or current-posture totals.
 */
export default function AssessmentCoverageCard({
  assessment,
  compact = false,
}: Props) {
  const { t } = useI18n()
  if (!assessment) return null

  const counts = assessment.counts || {
    uniqueFindings: assessment.findings?.total ?? 0,
    affectedInstances: assessment.findings?.total ?? 0,
    rawOccurrences: assessment.findings?.total ?? 0,
    critical: assessment.findings?.critical ?? 0,
    high: assessment.findings?.high ?? 0,
    medium: assessment.findings?.medium ?? 0,
    low: assessment.findings?.low ?? 0,
    info: assessment.findings?.info ?? 0,
  }
  const coverage = assessment.coverage || {}
  const scanner =
    assessment.scanner ||
    (assessment.provider
      ? formatScannerSourceLabel(assessment.provider)
      : assessment.providerType
        ? formatScannerSourceLabel(assessment.providerType)
        : 'Scanner')
  const durationLabel = fmtDuration(assessment.durationSeconds)

  return (
    <section className={entStyles.layer} data-testid="assessment-coverage-card">
      <p className={entStyles.layerLabel}>
        {t('reports.assessmentSummary', 'Assessment Summary')}
      </p>
      <p className={entStyles.postureStory} style={{ marginBottom: '0.75rem' }}>
        {t(
          'reports.assessmentSummarySubtitle',
          'Identity, coverage, and finding counts from this scanner execution only.',
        )}
      </p>
      <div className={entStyles.posturePanel}>
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.scanner', 'Scanner')}</p>
            <p className={entStyles.postureMetricValue}>{scanner}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.target', 'Target')}</p>
            <p className={entStyles.postureMetricValue}>{assessment.target || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.profile', 'Profile')}</p>
            <p className={entStyles.postureMetricValue}>
              {assessment.profile || assessment.templateProfile || assessment.scanType || '—'}
            </p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.statusLabel', 'Status')}</p>
            <p className={entStyles.postureMetricValue}>{assessment.status || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.parserStatus', 'Parser')}</p>
            <p className={entStyles.postureMetricValue}>{assessment.parserStatus || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.started', 'Started')}</p>
            <p className={entStyles.postureMetricValue}>{fmtDate(assessment.startedAt)}</p>
          </div>
          {durationLabel ? (
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{t('reports.duration', 'Duration')}</p>
              <p className={entStyles.postureMetricValue}>{durationLabel}</p>
            </div>
          ) : null}
        </div>

        <p className={entStyles.layerLabel} style={{ marginTop: '1rem' }}>
          {t('reports.coverage', 'Coverage')}
        </p>
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.analyzedUrls', 'Analyzed URLs')}
            </p>
            <p className={entStyles.postureMetricValue}>
              {fmtMetric((coverage as any).analyzedUrls ?? (coverage as any).analyzedUrlsCount)}
            </p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.pagesCrawled', 'Pages Crawled')}
            </p>
            <p className={entStyles.postureMetricValue}>{fmtMetric(coverage.pagesCrawled)}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.requestsSent', 'Requests Sent')}
            </p>
            <p className={entStyles.postureMetricValue}>{fmtMetric(coverage.requestsSent)}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.analyzedHosts', 'Analyzed Hosts')}
            </p>
            <p className={entStyles.postureMetricValue}>
              {fmtMetric((coverage as any).analyzedHosts ?? (coverage as any).analyzedHostsCount)}
            </p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.analyzedTargets', 'Analyzed Targets')}
            </p>
            <p className={entStyles.postureMetricValue}>
              {fmtMetric(
                (coverage as any).analyzedTargets ?? (coverage as any).analyzedTargetsCount,
              )}
            </p>
          </div>
        </div>

        <p className={entStyles.layerLabel} style={{ marginTop: '1rem' }}>
          {t('reports.findingCounts', 'Finding counts')}
        </p>
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.uniqueFindings', 'Unique findings')}
            </p>
            <p className={entStyles.postureMetricValue}>{counts.uniqueFindings ?? 0}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.affectedInstances', 'Affected instances')}
            </p>
            <p className={entStyles.postureMetricValue}>{counts.affectedInstances ?? 0}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.rawOccurrences', 'Raw occurrences')}
            </p>
            <p className={entStyles.postureMetricValue}>{counts.rawOccurrences ?? 0}</p>
          </div>
        </div>

        <p className={entStyles.layerLabel} style={{ marginTop: '1rem' }}>
          {t('reports.severity', 'Severity')}
        </p>
        <div className={entStyles.postureGrid}>
          {(
            [
              [t('severity.critical', 'Critical'), counts.critical ?? 0],
              [t('severity.high', 'High'), counts.high ?? 0],
              [t('severity.medium', 'Medium'), counts.medium ?? 0],
              [t('severity.low', 'Low'), counts.low ?? 0],
              [t('severity.info', 'Info'), counts.info ?? 0],
            ] as const
          ).map(([label, val]) => (
            <div key={label} className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>{label}</p>
              <p className={entStyles.postureMetricValue}>{val}</p>
            </div>
          ))}
        </div>

        {!compact && (counts.uniqueFindings ?? 0) === 0 ? (
          <p className={entStyles.postureStory} style={{ marginTop: '0.75rem' }}>
            {assessment.zeroFindingsMessage ||
              t(
                'reports.zeroFindingsSuccess',
                'This scan completed successfully and reported no findings.',
              )}
          </p>
        ) : null}
        {!compact && assessment.authoritativeZero ? (
          <p className={entStyles.postureStory} style={{ marginTop: '0.75rem' }}>
            {t(
              'reports.authoritativeZero',
              'Authoritative zero findings — the scanner completed coverage and reported no vulnerabilities.',
            )}
          </p>
        ) : null}
      </div>
    </section>
  )
}

type ContextProps = {
  title: string
  subtitle: string
  children: ReactNode
  defaultOpen?: boolean
  testId?: string
}

/** Collapsible secondary scope block (Current Posture / Historical / Technical). */
export function AssessmentScopeContextPanel({
  title,
  subtitle,
  children,
  defaultOpen = false,
  testId,
}: ContextProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className={`${entStyles.layer} ${entStyles.scopeContextBlock}`} data-testid={testId}>
      <button
        type="button"
        className="flex w-full flex-wrap items-center justify-between gap-2 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div>
          <p className={entStyles.layerLabel}>{title}</p>
          <p className={entStyles.scopeContextExplain}>{subtitle}</p>
        </div>
        <span className={`${entStyles.badge} ${entStyles.scopeContextToggle}`}>{open ? '−' : '+'}</span>
      </button>
      {open ? <div style={{ marginTop: '0.75rem' }}>{children}</div> : null}
    </section>
  )
}
